// Whether a contractor delivers finished web + print files rather than RAWs.
//
// This is Contractor.editingPreference === "self" (Option A): the MEP upload
// step is skipped and revisions route back to the contractor. "mep" (Option B)
// means they upload RAWs into the editor tab → HDR merge → category review →
// AI edit flow instead.
//
// DEPRECATED FALLBACK — the two lists below predate the editingPreference
// field, which was the only way to flag this before onboarding could capture
// it. They are consulted ONLY when no preference is set on the record, so the
// existing edited-photo photographers keep working until the backfill
// migration (beleef-services-backend/migrations) has run everywhere. Once
// every contractor doc carries a preference, delete both lists and the
// fallback branch.
//
// NOTE: this file is duplicated verbatim in beleef-admin-dashboard/src/utils/.
// Keep the two copies identical — the admin view branches on the same rule.
export const EDITED_PHOTO_CONTRACTOR_IDS = ["6a63011f590bb1e3c4da3b9d"];
export const EDITED_PHOTO_CONTRACTOR_EMAILS = ["kyle@capturedmedia.com.au"];

const PHOTO_SERVICE_KEYS = [
  "photo",
  "photoReshoot",
  "photoFreeShoot",
  "dusk",
  "drone",
  "duskGolden",
];

const idOf = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value._id || value.id || "");
};

export const contractorDeliversEditedPhotos = ({
  contractorId,
  email,
  user,
} = {}) => {
  const contractor = contractorId && typeof contractorId === "object"
    ? contractorId
    : null;

  // The event's assigned contractor is the subject, so their preference wins.
  // It's only readable when contractorId came through populated — in the admin
  // dashboard the viewer is NOT the contractor, so falling back to `user`
  // there would read the wrong person. `user` is the fallback for the
  // contractor-facing app, where the two are the same person.
  const preference = contractor?.editingPreference ?? user?.editingPreference;
  if (preference) return preference === "self";

  // No preference recorded — fall back to the pre-field allowlist.
  const ids = [idOf(contractorId), idOf(user)].filter(Boolean);
  if (ids.some((id) => EDITED_PHOTO_CONTRACTOR_IDS.includes(id))) return true;
  const mail = (
    email ||
    contractor?.email ||
    user?.email ||
    ""
  ).toLowerCase();
  return EDITED_PHOTO_CONTRACTOR_EMAILS.includes(mail);
};

export const requiredEditedCount = (photoServices = []) =>
  photoServices
    .filter((svc) => PHOTO_SERVICE_KEYS.includes(svc.eventTypeKey))
    .reduce((sum, svc) => sum + (Number(svc.quantity) || 0), 0);

const pairStem = (name) =>
  String(name || "")
    .replace(/\.[^.]+$/, "")
    .replace(
      /[-_\s]*(web|print|hi-?res|high-?res|lo-?res|websize|printsize)$/i,
      "",
    )
    .toLowerCase()
    .trim();

const naturalNameCompare = (a, b) =>
  String(a || "").localeCompare(String(b || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });

export const pairWebPrintImages = (webImages = [], printImages = []) => {
  const prints = [...printImages];
  const used = new Set();
  const pairs = [];
  const sortedWeb = [...webImages].sort((a, b) =>
    naturalNameCompare(a.name, b.name),
  );

  for (const web of sortedWeb) {
    const stem = pairStem(web.name);
    if (!stem) continue;
    const idx = prints.findIndex(
      (print, i) => !used.has(i) && pairStem(print.name) === stem,
    );
    if (idx >= 0) {
      used.add(idx);
      pairs.push({ web, print: prints[idx] });
    }
  }

  const leftoverWeb = sortedWeb.filter(
    (web) => !pairs.some((pair) => pair.web === web),
  );
  const leftoverPrint = prints
    .filter((_, i) => !used.has(i))
    .sort((a, b) => naturalNameCompare(a.name, b.name));
  const n = Math.min(leftoverWeb.length, leftoverPrint.length);
  for (let i = 0; i < n; i += 1) {
    pairs.push({ web: leftoverWeb[i], print: leftoverPrint[i] });
  }

  return {
    pairs,
    unpairedWeb: leftoverWeb.slice(n),
    unpairedPrint: leftoverPrint.slice(n),
  };
};

export const folderField = (folder) => {
  if (folder === "web") return "editedWebPhotos";
  if (folder === "print") return "editedPrintPhotos";
  if (folder === "raw") return "rawPhotos";
  return "extraPhotos";
};
